import { ReactNode, useEffect, useState } from 'react';
import { ApolloClient, NormalizedCacheObject, useApolloClient } from '@apollo/client';
import styled from 'styled-components';
import { isSsr } from '@99designs/browser';
import { Box, PageHeading } from '@99designs/emu';
import { colors } from '@99designs/emu/settings';
import { Site } from '@99designs/site-navigation/lib/Site';
import { FadeTransition } from '../components/FadeTransition';
import { Inspiration } from '../components/Inspiration';
import { HeaderNav } from '../components/Onboarder/HeaderNav';
import { LoadingSection } from '../components/Onboarder/LoadingSection';
import { useOnboarder } from '../components/Onboarder/OnboarderContext';
import { useRouteTransition } from '../components/RouteTransitionProvider';
import { Support } from '../components/Support';
import { HowItWorks } from './HowItWorks';
import { recommend } from './RecommendationEngine';
import { Recommendations } from './Recommendations';
import { mapFilterTags } from './mapFilterTags';
import { getProducts } from './products';
import { useProductConfig } from './useProductConfig';
import { getDesignInspirations } from './utils/getDesignInspirations';
import type { Design } from '@99designs/feature-design-inspiration';
import type { ProductKey } from '@99designs/marketing-ui';
import type { Role } from '@99designs/site-navigation/consts';
import type { SelectedValues } from './config';

const HeaderWrapper = styled(Box)`
    background-color: ${colors.grey100};
    background-image: linear-gradient(${colors.white}, ${colors.grey100});
`;

const OverflowWrapper = styled(Box)`
    overflow: hidden;
`;

const LoadingFadeTransition = styled.div`
    animation: fadeInAnimation ease 0.5s 1;

    @keyframes fadeInAnimation {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
`;

interface Props {
    role: Role;
    site: Site;
    Layout: React.ComponentType<{ site: Site; role: Role; children: ReactNode }>;
}

export function RecommendationPage({ role, site, Layout }: Props) {
    const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;
    const { data, config } = useOnboarder<SelectedValues>();
    const recommendedProductKeys = recommend(mapFilterTags(data));
    const recommendedProducts = getProducts(recommendedProductKeys as ProductKey[]);

    const recommendedProductsCategoryKeys = recommendedProducts.map((product) => {
        if (product.categoryKey === 'facebook-creative') return 'facebook-ad';
        if (
            product.categoryKey === 'logo-website-squarespace' ||
            product.categoryKey === 'squarespace-web-commerce'
        )
            return 'squarespace';

        return product.categoryKey;
    });

    const { transitionState, enableTransition } = useRouteTransition();
    const [isLoading, setIsLoading] = useState(true);
    const [designInspirationsLoading, setDesignInspirationsLoading] = useState<boolean>(false);
    const [designInspirations, setDesignInspirations] = useState<Design[]>([]);

    const populatedRecommendedProducts = useProductConfig(
        recommendedProducts,
        config.currency,
        config.exchangeRate
    );

    const fetchDesignInspirations = async (categoryKeys: string[], industries: string[]) => {
        setDesignInspirationsLoading(true);

        const data = await getDesignInspirations(apolloClient, categoryKeys, industries);

        setDesignInspirations(data);
        setDesignInspirationsLoading(false);
    };

    if (!isSsr() && !designInspirationsLoading && designInspirations.length === 0) {
        fetchDesignInspirations(
            recommendedProductsCategoryKeys,
            data.industry ? [data.industry] : []
        );
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 2000);
        return () => {
            enableTransition(500);
            clearTimeout(timer);
        };
    });

    if (isLoading) {
        return (
            <>
                <HeaderNav userRole={role} />
                <FadeTransition state={transitionState}>
                    <LoadingSection />
                </FadeTransition>
            </>
        );
    }

    return (
        <div className="max--widescreen transparent-site-header">
            <LoadingFadeTransition>
                <Layout site={site} role={role}>
                    <HeaderWrapper
                        paddingTop={['1.5bu', '1.5bu', '4bu', '4bu']}
                        paddingBottom="2bu"
                        display="flex"
                        justifyContent="center"
                    >
                        <Box
                            width="100%"
                            maxWidth="1300px"
                            paddingTop={['4bu', '4bu', '2bu', '2bu']}
                            paddingX="2bu"
                        >
                            <PageHeading>
                                {__('To start your brand today, we recommend…')}
                            </PageHeading>
                        </Box>
                    </HeaderWrapper>

                    <Recommendations recommendedProducts={populatedRecommendedProducts} />

                    <OverflowWrapper
                        marginTop={['3bu', '8bu']}
                        paddingX="2bu"
                        marginBottom={['3bu', '8bu']}
                    >
                        <HowItWorks />
                    </OverflowWrapper>

                    {designInspirations.length > 0 && (
                        <Box marginTop="6bu" marginBottom={['3bu', '6bu']}>
                            <Inspiration
                                categoryKeys={recommendedProductsCategoryKeys}
                                designs={designInspirations}
                                industry={data.industry}
                            />
                        </Box>
                    )}

                    <OverflowWrapper
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        width="100%"
                    >
                        <Box
                            marginTop={['3bu', '8bu']}
                            paddingX="2bu"
                            maxWidth="590px"
                            marginBottom={'8bu'}
                        >
                            <Support site={site} />
                        </Box>
                    </OverflowWrapper>
                </Layout>
            </LoadingFadeTransition>
        </div>
    );
}
